import cx from "classnames";
import React, { memo, useCallback } from "react";

import { useDashboardUnsaved } from "../../../../../../hook/useDashboardUnsaved";
import { Markdown } from "@ui/Markdown";
import { ToolbarSeparator } from "@ui/Markdown/Toolbar/MarkdownToolbar";
import { TableToolbarButton } from "@ui/Markdown/plugins/Table/components/TableToolbarButton";
import TabFocusPlugin from "@ui/Markdown/plugins/Table/plugins/TableFocusPlugin";
import { TableCellResizerPlugin } from "@ui/Markdown/plugins/Table/plugins/TableCellResizerPlugin";
import { DEFAULT_CLASSNAME_THEME_TABLE } from "@ui/Markdown/plugins/Table/utils/style";
import { DEFAULT_CONFIG_TABLE } from "@ui/Markdown/plugins/Table/utils/config";
import { MarkdownToolbarTable } from "@ui/Markdown/Toolbar/MarkdownToolbarTable";
import {
  convertFromMarkdown,
  MarkdownConverterPlugin,
} from "@ui/Markdown/plugins/common/MarkdownConverterPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { TablePasteSanitizerPlugin } from "@ui/Markdown/plugins/Table/plugins/TablePasteSanitizerPlugin";
import { EquationNode } from "@ui/Markdown/plugins/Equation/nodes/EquationNode";
import EquationPlugin from "@ui/Markdown/plugins/Equation/plugins/EquationPlugin";
import { EquationToolbarButton } from "@ui/Markdown/plugins/Equation/components/EquationToolbarButton";
import { DisableFormattingPlugin } from "@ui/Markdown/plugins/Table/plugins/DisableFormattingPlugin";

function RichText_({
  dashboardId,
  className,
  initialValue,
  onUpdate,
  placeholder = "Insert text here",
}: {
  dashboardId: string;
  className?: string;
  initialValue: string;
  onUpdate?: (value: string) => void;
  placeholder?: string;
}) {
  const [, setUnsaved] = useDashboardUnsaved({ scope: dashboardId });

  const onConversion = useCallback(
    (markdown: string) => {
      setUnsaved(true);
      onUpdate?.(markdown);
    },
    [onUpdate, setUnsaved],
  );

  return (
    <div className={cx(className)}>
      <Markdown
        placeholder={placeholder}
        config={{
          nodes: [EquationNode, ...DEFAULT_CONFIG_TABLE.nodes],
          theme: DEFAULT_CONFIG_TABLE.theme,
          editorState: () => convertFromMarkdown(initialValue),
        }}
        theme={DEFAULT_CLASSNAME_THEME_TABLE}
        editable
        autoFocus={false}
      >
        {richText => (
          <>
            <MarkdownToolbarTable>
              <ToolbarSeparator />
              <EquationToolbarButton />
              <TableToolbarButton />
            </MarkdownToolbarTable>
            {richText}
            <EquationPlugin />
            <TablePlugin hasHorizontalScroll />
            <TabFocusPlugin />
            <TableCellResizerPlugin />
            <TablePasteSanitizerPlugin />
            <DisableFormattingPlugin formats={["bold", "italic"]} />
            <MarkdownConverterPlugin onConversion={onConversion} />
          </>
        )}
      </Markdown>
    </div>
  );
}

export const RichText = memo(RichText_);
